/**
 * PopupWorker управляет работой Popup
 *
 * Должен грузить TopLibPopup только в отложенном режиме
 */

import Core from '@/core/core/core';
import DOM from '@/core/utils/dom';
import type { PopupOptions, TopLibPopup } from '@/components/popup/lib/popup';
import type { OpenerProps } from '@/components/popup/popup/opener/types';

export type Options = {
	/**
	 * Глобальное событие, которое будет вызвано перед открытием TopPopup
	 */
	decoratorBeforeOpen?: (popup: TopLibPopup) => void,

	/**
	 * Глобальное событие, которое будет вызвано после открытия TopPopup
	 */
	decoratorAfterOpen?: (popup: TopLibPopup) => void;

	/**
	 * Определитель того, что клик вне Popup нужно игнорировать
	 * @returns {boolean} - если true, то click не приведет к закрытию Popup
	 */
	decoratorIsIgnoreOuterClick?: (e: MouseEvent) => boolean
}

class Worker {

	/**
	 * Глобальное выключение закрытия меню, используется при различных оверлеях
	 */
	static noClose = false;

	/**
	 * Для взаимодействия с vue
	 */
	static vueConnectors = new Map();

	static options: Options = {};

	/**
	 * Вернуть объект компонента popup, привязанный к элементу
	 */
	static getPopup(elPopup: HTMLElement): TopLibPopup {
		return DOM.storage(elPopup, 'TopPopup') as TopLibPopup;
	}

	static getElPopupOpener(el: Element): HTMLElement | null {
		return el.closest<HTMLElement>('[data-top-popup]');
	}

	static getPopupFromOpener(el: HTMLElement): TopLibPopup | null {
		const elOpener = Worker.getElPopupOpener(el);
		if (!elOpener) return null;

		return DOM.storage(elOpener, '#TopPopup');
	}

	/**
	 * Вернуть все элементы с Popup
	 */
	static getAll(): HTMLElement[] {
		return DOM.querySelectorAllArray(document, '.top-popup-wrapper');
	}

	/**
	 * Вернуть все элементы с открытыми Popup
	 */
	static getAllVisible(): HTMLElement[] {
		return DOM.querySelectorAllVisible(document, '.top-popup-wrapper');
	}

	/**
	 * Открыть Popup от указанного elOpener
	 * @param {HTMLElement} elOpener - элемент, который должен вызвать открытие Popup
	 */
	static async openByOpener(elOpener: HTMLElement): Promise<TopLibPopup | undefined> {
		if (!(elOpener instanceof HTMLElement)) return;

		const options: PopupOptions = {};

		options.popup = elOpener.dataset.topPopup;

		options.id = elOpener.dataset.topPopupId;

		/**
		 * @deprecated в php шаблонах нет data-top-popup-id
		 */
		if (!options.id && options.popup) {
			elOpener.dataset.topPopupId = btoa(
				String.fromCharCode(...new TextEncoder().encode(options.popup))
			);

			options.id = elOpener.dataset.topPopupId;
		}

		/**
		 * @deprecated topPopupP - добавлено для обратной совместимости
		 */
		if (elOpener.dataset.topPopupP) {
			options.p = parseInt(elOpener.dataset.topPopupP);
		}

		if (elOpener.dataset.topPopupPos) {
			options.p = parseInt(elOpener.dataset.topPopupPos);
		}

		/**
		 * @deprecated '1' - добавлено для обратной совместимости
		 */
		if (elOpener.dataset.topPopupNotch) {
			options.notch = elOpener.dataset.topPopupNotch === 'true' || elOpener.dataset.topPopupNotch === '1';
		}

		if (elOpener.dataset.topPopupClass) {
			options.class = elOpener.dataset.topPopupClass;
		}

		if (elOpener.dataset.topPopupPosBy) {
			options.posBy = elOpener.dataset.topPopupPosBy as PopupOptions['posBy'];
		}

		if (elOpener.dataset.topPopupFront) {
			options.frontSelector = elOpener.dataset.topPopupFront;
		}

		/**
		 * @deprecated '1' - добавлено для обратной совместимости
		 */
		if (elOpener.dataset.topPopupInvertX) {
			options.invertX = elOpener.dataset.topPopupInvertX === 'true' || elOpener.dataset.topPopupInvertX === '1';
		}

		/**
		 * @deprecated '1' - добавлено для обратной совместимости
		 */
		if (elOpener.dataset.topPopupOpenByHovertopPopupOpenByHover) {
			options.openByHover = elOpener.dataset.topPopupOpenByHover === 'true' || elOpener.dataset.topPopupOpenByHover === '1';
		}

		/**
		 * @deprecated '1' - добавлено для обратной совместимости
		 */
		if (elOpener.dataset.topPopupUseOriginal) {
			options.useOriginal = elOpener.dataset.topPopupUseOriginal === 'true' || elOpener.dataset.topPopupUseOriginal === '1';
		}

		return await Worker.open(elOpener, options);
	}

	/**
	 * Создать и открыть Popup
	 * @param elOpener - элемент, вызвавший открытие Popup
	 * @param options - опции Popup
	 */
	private static async open(elOpener: HTMLElement, options: PopupOptions): Promise<TopLibPopup> {
		if (elOpener instanceof HTMLElement) elOpener.dataset.topPopupOpened = 'opened';

		const { TopLibPopup } = await import('./popup');

		if (Core.$) {
			const $elOpener = Core.$(elOpener);

			// deprecated, используется aftershow.top-menu-popup
			if ($elOpener.data('aftershow')) {
				let afterShow = $elOpener.data('aftershow');
				if (typeof (afterShow) == 'string') {
					// TODO: Удалить с сайта указание кода функции в html
					// @deprecated
					afterShow = eval(afterShow.replace(/\W/g, ''));
				}

				$elOpener.one('aftershow.top-menu-popup', afterShow);
			}

			// deprecated, используется afterclose.top-menu-popup
			if ($elOpener.data('afterclose')) {
				let afterclose = $elOpener.data('afterclose');
				if (typeof (afterclose) == 'string') {
					// TODO: Удалить с сайта указание кода функции в html
					// @deprecated
					afterclose = eval(afterclose.replace(/\W/g, ''));
				}

				$elOpener.one('afterclose.top-menu-popup', afterclose);
			}
		}

		options.isFullScreen = Core.state.isMobile;

		return new TopLibPopup(elOpener, options);
	}

	static close(elPopup: HTMLElement, restoreFocus = false): void {
		const popup = Worker.getPopup(elPopup);

		if (popup) {
			popup.close();

			const isFocused = popup.el.contains(document.activeElement);

			if (restoreFocus && !isFocused) {
				setTimeout(() => {
					popup.el.focus();
				});
			}
		}
	}

	static recalcPosition(elPopup: HTMLElement): void {
		const popup = Worker.getPopup(elPopup);
		if (popup && popup.elPopupBody instanceof HTMLElement) {
			popup.recalcPosition();
		}
	}

	/**
	 * Скролл к активному элементу списка
	 */
	static scrollToActive(elPopup: HTMLElement): void {
		const elItemActive = DOM.querySelectorVisible(elPopup, 'ul.top-popup_content > li > .top-active');
		if (!elItemActive) {
			return;
		}

		const elUl = elPopup.querySelector('ul.top-popup_content');
		const elsLi = DOM.querySelectorAllVisible(elPopup, 'ul.top-popup_content > li');

		elPopup.classList.add('top-popup-wrapper-no_animate');
		setTimeout(() => elPopup.classList.remove('top-popup-wrapper-no_animate'), 100);

		let top = 100;
		for (const elLi of elsLi) {
			if (elLi.querySelector(':scope > .top-active')) {
				break;
			}

			if (elLi instanceof HTMLElement) top += elLi.offsetHeight;
		}

		if (elUl instanceof HTMLElement) {
			const heightWrapper = elUl.offsetHeight;
			if (top > heightWrapper) {
				elUl.scrollTop = top - heightWrapper;
			} else {
				elUl.scrollTop = 0;
			}
		}
	}

	static decoratorBeforeOpen(popup: TopLibPopup<any>): void {
		if (this.options.decoratorBeforeOpen) {
			this.options.decoratorBeforeOpen(popup);
		}
	};

	static decoratorAfterOpen(popup: TopLibPopup<any>): void {
		if (this.options.decoratorAfterOpen) {
			this.options.decoratorAfterOpen(popup);
		}
	};

	static decoratorIsIgnoreOuterClick(e: MouseEvent): boolean {
		if (this.options.decoratorIsIgnoreOuterClick) {
			return this.options.decoratorIsIgnoreOuterClick(e);
		}

		return false;
	}

	/**
	 * Зарегистрировать Vue компонент
	 *
	 * Каждый экземляр Popup регистрируется в словаре vueConnectors для последующего доступа из js
	 */
	static regVueComponent(id: string, vueConnector: any): void {
		if (Worker.vueConnectors.get(id)) {
			throw 'Vue component Popup use duplicated id: ' + id;
		}

		Worker.vueConnectors.set(id, vueConnector);
	}

	/**
	 * Снять с регистрации Vue компонент
	 */
	static unregVueComponent(id: string): void {
		Worker.vueConnectors.delete(id);
	}

	/**
	 * Сгенерировать элемент для открытия popup
	 */
	static genElPopupOpener(tagName: string, props: OpenerProps, ...elsChildrens: HTMLElement[] | string[]): HTMLElement {
		const el = DOM.genEl(tagName, {}, ...elsChildrens) as TopLibPopup['el'];

		el.dataset.topPopup = '#' + props.id;

		// if (props.pos === undefined) props.pos = '3';
		// if (props.notch === undefined) props.notch = true;
		// if (props.posBy === undefined) props.posBy = 'fixed';

		for (let name in props) {
			const attrName = 'topPopup' + name.charAt(0).toUpperCase() + name.slice(1);

			el.dataset[attrName] = props[name];
		}

		el.__TopPopupOpenerProps = props;

		return el;
	}

}

export default Worker;
